import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';

import trackAction from 'utils/tracking';
import { buildMapsUrl } from 'utils/url';

import IconSvg from 'components/Core/IconSvg';

import {
  FooterWrap,
  FooterElem,
  FooterTop,
  FooterLogoAnchor,
  FooterContact,
  FooterContactItem,
  FooterAddressItem,
  FooterBottom,
  FooterNav,
  FooterAnchor,
  FooterSocialItems,
} from './Footer.styles';

const Footer = (props) => {
  const {
    footerType,
    streetName,
    houseNumber,
    postCode,
    city,
    telephone,
    email,
    twitterUrl,
    facebookUrl,
    instagramUrl,
    tiktokUrl,
    linkedinUrl,
    publicLegalPages,
    siteLogo,
    siteName,
  } = props;

  const currentYear = new Date().getFullYear();
  const isAccommodation = footerType === 'accommodation';
  const showAddress = isAccommodation && streetName && houseNumber && postCode && city;
  const showContactInfo = isAccommodation && (telephone || email);
  const showSocialIcons = (twitterUrl || facebookUrl || instagramUrl) !== null;

  const trackFooterLinkClick = (label) => trackAction('footer', label, 'click');

  return (
    <FooterWrap data-test-id="component-footer">
      <FooterElem itemScope itemType="https://schema.org/Organization">
        <FooterTop>
          <Link href="/" passHref>
            <FooterLogoAnchor role="link" aria-label={siteName} alt={siteName}>
              <img src={siteLogo.md.url} className="max-w-full" alt={siteLogo.title} data-test-id="img-logo" />
            </FooterLogoAnchor>
          </Link>
        </FooterTop>

        <FooterContact isAccommodation={isAccommodation}>
          {showAddress && (
            <FooterAddressItem
              href={buildMapsUrl(siteName, streetName, houseNumber, postCode, city)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-wrap flex-row justify-center lg:flex-no-wrap lg:flex-col text-primary"
              itemProp="address"
              itemScope
              itemType="https://schema.org/PostalAddress"
              data-test-id="address"
              isAccommodation={isAccommodation}
            >
              <div
                itemProp="streetAddress"
                className="pr-2 leading-4 h-4 border-primary border-r lg:border-0 lg:leading-6 lg:h-auto"
              >{`${streetName} ${houseNumber}`}</div>
              <div className="pl-2 leading-4 h-4 lg:leading-7 lg:h-auto">
                <span itemProp="postalCode">{postCode}</span> <span itemProp="addressLocality">{city}</span>
              </div>
            </FooterAddressItem>
          )}
          {showContactInfo && (
            <FooterContactItem className="pt-2 lg:pt-0" data-test-id="contact-info" isAccommodation={isAccommodation}>
              {telephone && (
                <div>
                  <FooterAnchor
                    itemProp="telephone"
                    href={`tel:${telephone}`}
                    onClick={() => trackFooterLinkClick('telephone')}
                    onKeyPress={() => trackFooterLinkClick('telephone')}
                  >
                    {telephone}
                  </FooterAnchor>
                </div>
              )}
              {email && (
                <div>
                  <FooterAnchor
                    itemProp="email"
                    href={`mailto:${email}`}
                    onClick={() => trackFooterLinkClick('email')}
                    onKeyPress={() => trackFooterLinkClick('email')}
                  >
                    {email}
                  </FooterAnchor>
                </div>
              )}
            </FooterContactItem>
          )}
          {showSocialIcons && (
            <FooterSocialItems isAccommodation={isAccommodation} data-test-id="social">
              {twitterUrl && (
                <FooterAnchor
                  href={twitterUrl}
                  target="_blank"
                  rel="noreferrer"
                  role="link"
                  alt="twitter"
                  aria-label="Twitter"
                  onClick={() => trackFooterLinkClick('twitter')}
                  onKeyPress={() => trackFooterLinkClick('twitter')}
                  className="mr-10 lg:mr-8 inline-block text-primary"
                >
                  <IconSvg icon="social-twitter" className="h-6 w-6" />
                </FooterAnchor>
              )}
              {facebookUrl && (
                <FooterAnchor
                  href={facebookUrl}
                  target="_blank"
                  rel="noreferrer"
                  role="link"
                  alt="Facebook"
                  aria-label="Facebook"
                  onClick={() => trackFooterLinkClick('facebook')}
                  onKeyPress={() => trackFooterLinkClick('facebook')}
                  className="mr-10 lg:mr-8 inline-block text-primary"
                >
                  <IconSvg icon="social-facebook" className="h-6 w-6 " />
                </FooterAnchor>
              )}
              {instagramUrl && (
                <FooterAnchor
                  href={instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  role="link"
                  alt="Instagram"
                  aria-label="Instagram"
                  onClick={() => trackFooterLinkClick('instagram')}
                  onKeyPress={() => trackFooterLinkClick('instagram')}
                  className="mr-10 lg:mr-8 inline-block text-primary"
                >
                  <IconSvg icon="social-instagram" classNames="h-6 w-6" />
                </FooterAnchor>
              )}
              {tiktokUrl && (
                <FooterAnchor
                  href={tiktokUrl}
                  target="_blank"
                  rel="noreferrer"
                  role="link"
                  alt="TikTok"
                  aria-label="TikTok"
                  onClick={() => trackFooterLinkClick('tiktok')}
                  onKeyPress={() => trackFooterLinkClick('tiktok')}
                  className="mr-10 lg:mr-8 inline-block text-primary"
                >
                  <IconSvg icon="social-tiktok" classNames="h-6 w-6" />
                </FooterAnchor>
              )}
              {linkedinUrl && (
                <FooterAnchor
                  href={linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  role="link"
                  alt="LinkedIn"
                  aria-label="LinkedIn"
                  onClick={() => trackFooterLinkClick('linkedin')}
                  onKeyPress={() => trackFooterLinkClick('linkedin')}
                  className="inline-block text-primary"
                >
                  <IconSvg icon="social-linkedin" classNames="h-6 w-6" />
                </FooterAnchor>
              )}
            </FooterSocialItems>
          )}
        </FooterContact>

        <FooterBottom>
          <div>{`© Sportfondsen ${currentYear}`}</div>
          <FooterNav data-test-id="nav">
            {publicLegalPages.map((item) => (
              <Link href="/[...slug]" as={item.page.url} key={`legalPage-${item.page.id}`} passHref>
                <FooterAnchor
                  role="link"
                  aria-label={item.page.title}
                  alt={item.page.title}
                  onClick={() => trackFooterLinkClick(item.page.title)}
                  onKeyPress={() => trackFooterLinkClick(item.page.title)}
                  className="px-2 border-r leading-4 h-4 space-x-4"
                >
                  {item.page.title}
                </FooterAnchor>
              </Link>
            ))}
          </FooterNav>
        </FooterBottom>
      </FooterElem>
    </FooterWrap>
  );
};

Footer.defaultProps = {
  footerType: 'accommodation',
  streetName: null,
  houseNumber: null,
  postCode: null,
  city: null,
  telephone: null,
  email: null,
  twitterUrl: null,
  facebookUrl: null,
  instagramUrl: null,
  tiktokUrl: null,
  linkedinUrl: null,
  publicLegalPages: [],
  siteName: '',
  siteLogo: {
    title: 'Sportfondsen logo',
    sm: {
      url: '/assets/logo_sportfondsen.png',
    },
    md: {
      url: '/assets/logo_sportfondsen.png',
    },
  },
};

Footer.propTypes = {
  footerType: PropTypes.oneOf(['accommodation', 'sportfondsen']),
  streetName: PropTypes.string,
  houseNumber: PropTypes.string,
  postCode: PropTypes.string,
  city: PropTypes.string,
  telephone: PropTypes.string,
  email: PropTypes.string,
  twitterUrl: PropTypes.string,
  facebookUrl: PropTypes.string,
  instagramUrl: PropTypes.string,
  tiktokUrl: PropTypes.string,
  linkedinUrl: PropTypes.string,
  publicLegalPages: PropTypes.arrayOf(
    PropTypes.shape({
      page: PropTypes.shape({
        title: PropTypes.string,
        id: PropTypes.string,
        url: PropTypes.string,
      }),
    }),
  ),
  siteLogo: PropTypes.shape({
    sm: PropTypes.shape({
      url: PropTypes.string,
    }),
    md: PropTypes.shape({
      url: PropTypes.string,
    }),
    title: PropTypes.string,
  }),
  siteName: PropTypes.string,
};

export default Footer;
